import React from 'react'
import axios from 'axios'
import styled, {
	ThemeProvider,
	createGlobalStyle,
} from 'styled-components/macro'
import {
	BrowserRouter as Router,
	Switch,
	Route,
	Redirect,
} from 'react-router-dom'
import Form from 'views/Form'
import CompanySearch from 'views/CompanySearch'
import Success from 'views/Success'
import Signing from 'views/Signing'
import defaultTheme from 'defaultTheme'
import { Spinner } from 'components'

const GlobalStyle = createGlobalStyle`
	html, body {
		margin: 0;
		font-family: 'Poppins', Arial, sans-serif;
		font-size: 17px;
		line-height: 1.6;
		color: ${props => props.theme.inkColor};
		background: ${props => props.theme.softColor};
		-webkit-font-smoothing: antialiased;
	}
	a {
		color: ${props => props.theme.primaryColor};
	}
`

const Header = styled.header`
	background: #fff;
	border-bottom: 1px solid ${props => props.theme.lineColor};
	> div {
		max-width: 1200px;
		margin: 0 auto;
		padding: 18px 28px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
	}
	img {
		display: block;
		width: 200px;
		height: auto;
	}
	@media (max-width: 600px) {
		> div {
			padding: 14px 16px;
		}
		img {
			width: 150px;
		}
	}
`

const Band = styled.div`
	background: ${props => props.theme.navyColor};
	color: #fff;
	padding: 56px 28px 120px;
	> div {
		max-width: 820px;
		margin: 0 auto;
	}
	h1 {
		margin: 0 0 10px;
		font-size: clamp(30px, 4.4vw, 46px);
		line-height: 1.1;
		letter-spacing: -0.03em;
		font-weight: 700;
	}
	p {
		margin: 0;
		font-size: 18px;
		color: rgba(255, 255, 255, 0.8);
	}
	@media (max-width: 600px) {
		padding: 36px 16px 96px;
	}
`

const Footer = styled.footer`
	max-width: 820px;
	margin: 0 auto;
	padding: 8px 28px 48px;
	font-size: 14px;
	line-height: 1.7;
	color: ${props => props.theme.mutedColor};
	a {
		color: ${props => props.theme.mutedColor};
	}
	@media (max-width: 600px) {
		padding: 0 16px 36px;
	}
`

const DemoBanner = styled.div`
	background: #fff3cd;
	border: 1px solid #ffc107;
	color: #664d03;
	padding: 12px 16px;
	margin: 0 auto;
	font-size: 0.9rem;
	line-height: 1.4;
	box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
`

const FormContainer = styled.main`
	box-sizing: border-box;
	background: #fff;
	padding: 48px 56px 40px;
	max-width: 820px;
	margin: -84px auto 32px;
	border-radius: ${props => props.theme.radius};
	border: 1px solid ${props => props.theme.lineColor};
	box-shadow: 0 10px 30px rgba(11, 46, 77, 0.08);
	overflow: hidden;
	h1,
	h2 {
		margin: 0 0 14px;
		font-size: 26px;
		line-height: 1.2;
		letter-spacing: -0.02em;
		font-weight: 700;
		color: ${props => props.theme.navyColor};
	}
	p {
		margin: 0 0 16px;
	}
	p.lead {
		font-size: 18px;
		color: ${props => props.theme.mutedColor};
		margin-bottom: 26px;
	}
	@media (max-width: 860px) {
		margin-left: 16px;
		margin-right: 16px;
	}
	@media (max-width: 600px) {
		padding: 28px 20px 24px;
		margin-top: -72px;
		h1,
		h2 {
			font-size: 22px;
		}
	}
`

const NewForm = ({ location }) => {
	const [status, setStatus] = React.useState('loading')
	const [error, setError] = React.useState(null)
	const [id, setId] = React.useState('')
	const companyId = location?.state?.companyId || ''

	const createForm = () => {
		const stored = localStorage.getItem('applicationInfo')
		if (stored && stored.appId && stored.companyId === companyId) {
			setId(stored)
			setStatus('success')
			return
		}
		if (companyId === '') {
			return
		}

		axios
			.post(`/api/applications`, { companyId })
			.then(res => {
				setId(res.data.id)
				setStatus('success')
				localStorage.setItem('applicationInfo', {
					appId: res.data.id,
					companyId,
				})
			})
			.catch(err => {
				setError(err)
			})
	}

	React.useEffect(createForm, [companyId])

	if (!companyId) {
		return <Redirect to="/" />
	}

	if (status === 'success' && id !== '') {
		return <Redirect to={`/form/${id}`} />
	}

	if (status === 'loading' && !error) {
		return (
			<Spinner
				block
				size={34}
				text="Haetaan yrityksen tietoja, tämä kestää muutaman sekunnin…"
			/>
		)
	}

	return <p>Hakemuksen luonti epäonnistui. Palaa hakuun ja yritä uudelleen.</p>
}

function App() {
	const [siteConfig, setSiteConfig] = React.useState({
		demoMode: false,
		simulatePayments: false,
		appEnv: 'prod',
	})

	React.useEffect(() => {
		axios
			.get('/api/site-config')
			.then(res => setSiteConfig(res.data))
			.catch(() => {})
	}, [])

	return (
		<ThemeProvider theme={defaultTheme}>
			<GlobalStyle />
			{siteConfig.demoMode && (
				<DemoBanner>
					<strong>Demoversio.</strong> Voit testata koko prosessin (maksu,
					allekirjoitus, dokumentit). Hakemusta ei lähetetä käräjäoikeudelle.
					{siteConfig.simulatePayments
						? ' Maksu simuloidaan.'
						: ' Maksu tapahtuu Paytrailin kautta (testihinta).'}{' '}
					Allekirjoituskutsut lähtevät Visma Signista.
				</DemoBanner>
			)}
			<Header>
				<div>
					<a href="https://yrityskorjaamo.fi">
						<img alt="Yrityskorjaamo – etusivulle" src="/yrityskorjaamo_logo.jpg" />
					</a>
				</div>
			</Header>
			<Band>
				<div>
					<h1>Yrityssaneeraus&shy;hakemus</h1>
					<p>Osakeyhtiön hakemus käräjäoikeuteen · 159 € + alv</p>
				</div>
			</Band>
			<FormContainer>
				<Router>
					<Switch>
						<Route path="/" exact component={CompanySearch} />
						<Route path="/form/new" component={NewForm} />
						<Route path="/form/:applicationId/success" component={Success} />
						<Route path="/form/:applicationId/signing" component={Signing} />
						<Route path="/form/:applicationId" component={Form} />
					</Switch>
				</Router>
			</FormContainer>
			<Footer>
				Yrityskorjaamo on Rand Customer Service Oy:n aputoiminimi · Y-tunnus
				2427527-9 · yrityssaneeraus@yrityskorjaamo.fi
				<br />
				<a
					href="https://yrityskorjaamo.fi/yleiset-palveluehdot/"
					target="_blank"
					rel="noopener noreferrer"
				>
					Palveluehdot
				</a>{' '}
				·{' '}
				<a
					href="https://yrityskorjaamo.fi/tietosuojaseloste/"
					target="_blank"
					rel="noopener noreferrer"
				>
					Tietosuojaseloste
				</a>{' '}
				·{' '}
				<a
					href="https://yrityskorjaamo.fi/palvelukuvaus/"
					target="_blank"
					rel="noopener noreferrer"
				>
					Palvelukuvaus
				</a>
			</Footer>
		</ThemeProvider>
	)
}

export default App
